import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, dateOfHireInitialQuestion, initialExemptionQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function MichiganForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const dateOfHireForm = useRef();
  const dateOfHireQuestionWithAnswer = getInitialQuestions(dateOfHireInitialQuestion);
  const [dateOfHireQuestion, setDateOfHireQuestion] = useState(dateOfHireQuestionWithAnswer);

  const exemptionForm = useRef();
  const exemptionQuestionsWithAnswer = getInitialQuestions(initialExemptionQuestions);
  const [exemptionQuestions, setExemptionQuestions] = useState(exemptionQuestionsWithAnswer);

  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const [exemptionReason, setExemptionReason] = useState(data.exemption_from_withholding_two_reason || '');
  const [exemptionReasonError, setExemptionReasonError] = useState(false);
  const [renaissanceZone, setRenaissanceZone] = useState(data.renaissance_zone || '');
  const [renaissanceZoneError, setRenaissanceZoneError] = useState(false);

  const isNewEmployee = () => stepOneQuestions[8].answer.answer.new_employee === 'new_employee_yes';

  const renderDateOfHire = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={dateOfHireQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = dateOfHireQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setDateOfHireQuestion(questions);
        }}
        ref={dateOfHireForm}
      />
    </Flex>
  );

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      {isNewEmployee() && renderDateOfHire(isReviewStep)}
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderExemptionQuestions = (isReviewStep) => (
    <Flex column>
      <Text mb="15px" bold fontSize="14px">
        Exemptions
      </Text>
      <Text mb="20px">
        Enter your number of personal and dependency exemptions. The number of exemptions
        claimed here may not exceed the number of exemptions you are entitled to claim on
        a Michigan Individual Income Tax Return (Form MI-1040). Dependents include qualifying
        children and qualifying relatives under the Internal Revenue Code, even if your AGI
        exceeds the limits to claim federal tax credits for them.
      </Text>
      <Text mb="15px">
        Do not claim the same exemptions more than once or tax will be under-withheld.
        Specifically, do not claim:
      </Text>
      <Flex column mb="20px">
        <Text>
          {'\u2022'}
          {' '}
          Your personal exemption if someone else will claim you as their dependent.
        </Text>
        <Text>
          {'\u2022'}
          {' '}
          Your personal exemption with more than one employer at a time.
        </Text>
        <Text>
          {'\u2022'}
          {' '}
          Your spouse’s personal exemption if they claim it with their employer.
        </Text>
        <Text>
          {'\u2022'}
          {' '}
          Your dependency exemptions if someone else (for example, your spouse)
          is claiming them with their employer.
        </Text>
      </Flex>
      <FormConsumer
        questions={exemptionQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionQuestions(questions);
        }}
        ref={exemptionForm}
      />
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text mb="15px" bold fontSize="14px">
        Exemptions from withholding
      </Text>
      <S.QuestionWrapper mb="24px">
        <Flex mb="7px">
          <S.QuestionLabel isRequired={isExemptFromWithholding}>
            I claim exemption from withholding because:
          </S.QuestionLabel>
        </Flex>
        <S.Select
          disabled={isReviewStep}
          onChange={(e) => {
            setExemptionsFromWithholding(e.target.value);
          }}
          value={exemptionsFromWithholding}
        >
          <option value="" style={{ display: 'none', color: '#666' }} key={0}>
            Select from dropdown
          </option>
          <option value="exemption_from_withholding_one" key={1}>
            A Michigan income tax liability is not expected this year.
          </option>
          <option value="exemption_from_withholding_two" key={2}>
            Wages are exempt from withholding.
          </option>
          <option value="exemption_from_withholding_three" key={3}>
            Permanent home (domicile) is located in a Renaissance Zone.
          </option>
        </S.Select>
        {exemptionsError && exemptionsFromWithholding === '' && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      {exemptionsFromWithholding === 'exemption_from_withholding_two' && (
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel
            isRequired={isExemptFromWithholding
                  && exemptionsFromWithholding === 'exemption_from_withholding_two'}
          >
            Please explain:
          </S.QuestionLabel>
        </Flex>
        <Flex>
          <S.TextArea
            type="text"
            rows={5}
            value={exemptionReason}
            onChange={(e) => setExemptionReason(e.target.value)}
            disabled={isReviewStep}
          />
        </Flex>
        {exemptionReasonError && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      )}
      {exemptionsFromWithholding === 'exemption_from_withholding_three' && (
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel
            isRequired={isExemptFromWithholding
                  && exemptionsFromWithholding === 'exemption_from_withholding_three'}
          >
            Which Renaissance Zone?
          </S.QuestionLabel>
        </Flex>
        <Flex>
          <S.Input
            type="text"
            value={renaissanceZone}
            onChange={(e) => setRenaissanceZone(e.target.value)}
            disabled={isReviewStep}
          />
        </Flex>
        {renaissanceZoneError && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      )}
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderExemptionQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (isNewEmployee()) {
              if (dateOfHireForm && dateOfHireForm.current) {
                dateOfHireForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            }
            if (!isExemptFromWithholding) {
              if (exemptionForm && exemptionForm.current) {
                exemptionForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            } else {
              if (exemptionsFromWithholding === '') {
                err = true;
                setExemptionsError(true);
              } else {
                setExemptionsError(false);
              }
              if (exemptionsFromWithholding === 'exemption_from_withholding_two'
                    && exemptionReason == '') {
                err = true;
                setExemptionReasonError(true);
              } else {
                setExemptionReasonError(false);
              }
              if (exemptionsFromWithholding === 'exemption_from_withholding_three'
                    && renaissanceZone == '') {
                err = true;
                setRenaissanceZoneError(true);
              } else {
                setRenaissanceZoneError(false);
              }
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let dateOfHireData = resetInitialQuestions(dateOfHireInitialQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            if (isNewEmployee()) {
              dateOfHireData = dateOfHireQuestion.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            let exemptionQuestionsData = resetInitialQuestions(initialExemptionQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let exemptionsFromWithholdingTemp = '';
            let reasonTemp = '';
            let renaissanceZoneTemp = '';
            if (!isExemptFromWithholding) {
              exemptionQuestionsData = exemptionQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              exemptionsFromWithholdingTemp = exemptionsFromWithholding;
              if (exemptionsFromWithholding === 'exemption_from_withholding_two') {
                reasonTemp = exemptionReason;
              }
              if (exemptionsFromWithholding === 'exemption_from_withholding_three') {
                renaissanceZoneTemp = renaissanceZone;
              }
            }
            const exemptionData = {
              exemption: exemptionsFromWithholdingTemp,
              is_exempt: isExemptFromWithholding,
              exemption_from_withholding_two_reason: reasonTemp,
              renaissance_zone: renaissanceZoneTemp,
            };
            const data = {
              ...stepOneData, ...dateOfHireData, ...exemptionQuestionsData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderExemptionQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default MichiganForm;
